import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { X, Download, BookOpen, CheckCircle2 } from "lucide-react";
import { api, API } from "@/lib/api";
import { attribution, track } from "@/lib/analytics";

const CATALOG_IMG = "https://customer-assets-rejwkqb3.emergentagent.net/job_seniorcar-discover/artifacts/744417b78035a848_EADEF8AD-40D3-40B4-AB77-82BCB82C30FA.png";

export const CatalogPopup = () => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ first_name: "", email: "", privacy_consent: false });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const openIt = () => setOpen(true);
    const closeIt = () => setOpen(false);
    window.addEventListener("sc-open-catalog", openIt);
    window.addEventListener("sc-open-promo", closeIt);
    return () => {
      window.removeEventListener("sc-open-catalog", openIt);
      window.removeEventListener("sc-open-promo", closeIt);
    };
  }, []);

  const close = () => setOpen(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.email) return toast.error("Inserisci nome ed email.");
    if (!form.privacy_consent) return toast.error("È necessario accettare la privacy policy.");
    setLoading(true);
    try {
      await api.post("/lead-magnet", { ...form, phone: "", lead_type: "lead_magnet", requested_action: "catalog", ...attribution() });
      track("lead_form_submit", { form: "catalog_popup" });
      setDone(true);
      toast.success("Grazie! Il catalogo è pronto.");
    } catch { toast.error("Errore. Riprova."); }
    finally { setLoading(false); }
  };

  const download = () => {
    track("catalog_download", { source: "catalog_popup" });
    window.open(`${API}/catalog/download`, "_blank");
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1900] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm" data-testid="catalog-popup" onClick={close}>
      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300" onClick={(e) => e.stopPropagation()}>
        <button onClick={close} aria-label="Chiudi" data-testid="catalog-close"
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/90 flex items-center justify-center text-slate-600 hover:text-slate-900 shadow">
          <X size={18} />
        </button>

        <img src={CATALOG_IMG} alt="Catalogo informativo SENIORCAR aperto" loading="lazy"
          className="w-full h-44 object-cover" data-testid="catalog-image" />

        <div className="px-6 py-5">
          <div className="flex items-center gap-2">
            <span className="w-9 h-9 rounded-lg flex items-center justify-center text-white shrink-0" style={{ backgroundColor: "var(--sc-emerald)" }}><BookOpen size={18} /></span>
            <h2 className="text-xl font-extrabold text-slate-900 leading-tight" style={{ fontFamily: "Outfit" }}>Richiedi il catalogo informativo gratuito</h2>
          </div>
          <p className="mt-2 text-sm text-slate-600">Ricevi via email il catalogo completo di SENIORCAR: modelli, caratteristiche, agevolazioni e tutto ciò che ti serve per scegliere con serenità.</p>

          {done ? (
            <div className="text-center py-4" data-testid="catalog-success">
              <CheckCircle2 className="mx-auto text-emerald-700 mb-2" size={40} />
              <p className="font-semibold text-emerald-900 mb-4">Il tuo catalogo è pronto!</p>
              <Button onClick={download} className="h-12 px-6 font-bold text-white rounded-full gap-2" style={{ backgroundColor: "var(--sc-amber)" }} data-testid="catalog-download-button">
                <Download size={18} /> Scarica il catalogo (PDF)
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-4 space-y-3">
              <Input placeholder="Nome" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="h-12 bg-white" data-testid="catalog-name" />
              <Input type="email" placeholder="La tua email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-12 bg-white" data-testid="catalog-email" />
              <label className="flex items-start gap-2 text-sm text-slate-600">
                <Checkbox checked={form.privacy_consent} onCheckedChange={(v) => setForm({ ...form, privacy_consent: !!v })} className="mt-0.5" data-testid="catalog-privacy" />
                <span>Ho letto e accetto la <a href="/privacy" className="underline">Privacy Policy</a>.</span>
              </label>
              <Button type="submit" disabled={loading} className="w-full h-12 font-bold text-white rounded-full gap-2" style={{ backgroundColor: "var(--sc-emerald)" }} data-testid="catalog-submit">
                <BookOpen size={18} /> {loading ? "Invio..." : "Ricevi il catalogo gratuito"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
